import { useGetSystemOverviewQuery } from '../store/adminApi';
import { Card, StatCard } from '../components/Card';
import { ErrorState } from '../components/LoadingSpinner';
import { StatusBadge } from '../components/StatusBadge';
import { CardsSkeleton, Icon, PageHeader } from '../components/admin/AdminUI';

export function OverviewPage() {
  const { data, isLoading, error } = useGetSystemOverviewQuery(undefined, {
    pollingInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return <CardsSkeleton message="Loading system overview..." />;
  }

  if (error || !data) {
    return <ErrorState message="Failed to load system overview" />;
  }

  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="System Overview"
        subtitle={`Last updated: ${formatTime(data.time)}`}
        actions={
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live · refreshes every 30s
          </span>
        }
      />

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Database"
          icon="database"
          value={data.dbOk ? 'Connected' : 'Disconnected'}
          status={data.dbOk ? 'success' : 'error'}
        />
        <StatCard
          label="APNs (iOS)"
          icon="apple"
          value={data.apnsConfigured ? 'Configured' : 'Not Configured'}
          status={data.apnsConfigured ? 'success' : 'warning'}
        />
        <StatCard
          label="FCM (Android)"
          icon="android"
          value={data.fcmConfigured ? 'Configured' : 'Not Configured'}
          status={data.fcmConfigured ? 'success' : 'warning'}
        />
        <StatCard
          label="Device Tokens"
          icon="tokens"
          value={data.counts.deviceTokensTotal.toLocaleString()}
          status="neutral"
        />
      </div>

      {/* 24h Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label="Delivery Attempts (24h)"
          icon="attempts"
          value={data.counts.deliveryAttempts24h.toLocaleString()}
          status="neutral"
        />
        <StatCard
          label="Failed Deliveries (24h)"
          icon="alert"
          value={data.counts.deliveryFailed24h.toLocaleString()}
          status={data.counts.deliveryFailed24h > 0 ? 'warning' : 'success'}
        />
        <StatCard
          label="Success Rate (24h)"
          icon="chart"
          value={
            data.counts.deliveryAttempts24h > 0
              ? `${(
                  ((data.counts.deliveryAttempts24h - data.counts.deliveryFailed24h) /
                    data.counts.deliveryAttempts24h) *
                  100
                ).toFixed(1)}%`
              : 'N/A'
          }
          status={
            data.counts.deliveryAttempts24h === 0
              ? 'neutral'
              : data.counts.deliveryFailed24h / data.counts.deliveryAttempts24h < 0.05
              ? 'success'
              : 'warning'
          }
        />
      </div>

      {/* Recent Errors */}
      <Card title="Recent Errors (24h)" description="Provider failures from the last 24 hours">
        {data.recentErrors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-8 ring-emerald-50/50">
              <Icon name="check" className="h-6 w-6" />
            </span>
            <p className="mt-4 text-sm font-medium text-slate-900">No errors in the last 24 hours</p>
            <p className="mt-1 text-xs text-slate-500">Nothing needs attention right now.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 -my-2">
            {data.recentErrors.map((err, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 py-3"
              >
                <StatusBadge
                  status={err.provider === 'apns' ? 'info' : 'success'}
                  label={err.provider.toUpperCase()}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {err.errorCode && (
                      <span className="text-sm font-mono text-red-600">
                        {err.errorCode}
                      </span>
                    )}
                    {err.appId && (
                      <span className="text-xs text-slate-500">App: {err.appId}</span>
                    )}
                  </div>
                  {err.errorMessage && (
                    <p className="text-sm text-slate-600 mt-1 truncate">
                      {err.errorMessage}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    {formatTime(err.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
